/* eslint-disable no-console */
import { acceptHMRUpdate, defineStore } from 'pinia'
import * as Sentry from '@sentry/browser'
import { useLocalStorage } from '@vueuse/core'
import { isInternalUser } from '~/constants'
import { reloadPage } from '~/utils'
import type {
  GETOrganization,
  GETToken,
  GETUserInfo,
  SimpleUser,
} from '~/services/apiClient'
import {
  OrganizationSubscription,
  UserRoleType,
  UsersApi,
} from '~/services/apiClient'
import { UserInfo } from '~/models/userInfo'
import { Organization } from '~/models/organization'
import { clearChatData, configureUserWithCrispChat } from '~/services/crispChat'
import { startOpenReplayTracking } from '~/observability/openReplay'
import {
  clearAxiosAuthorizationHeader,
  getAxiosAuthorizationHeader,
  setAxiosAuthorizationHeader,
} from '~/services/apiHelpers'
import {
  broadcastOrgChangeToOtherTabs,
  clearOrgChangeState,
  setOrgChangeState,
} from '~/managers/orgChangeManagement'
import * as authRepo from '~/repositories/authentication'
import {
  AUTH_HEADER_KEY,
  NEW_AUTH_HEADER_KEY,
} from '~/repositories/authentication'
import * as userInfoRepo from '~/repositories/userInfo'

function clearLocalStorageExcept(keepKeys: Set<string>) {
  const allKeys = Object.keys(localStorage)
  allKeys.forEach((key) => {
    if (!keepKeys.has(key)) {
      localStorage.removeItem(key)
    }
  })
}

const AUTH_HEADERS_BY_ORG_ID_KEY = 'auth_headers_by_org_id'
const EXPORT_IN_TRINIUM_MODE_KEY = 'export_in_trinium_mode'
const EXPORT_ALL_APPOINTMENTS_KEY = 'export_all_appointments'
const USE_OWN_CHASSIS_KEY = 'use_own_chassis'
const DEMO_MODE_KEY = 'demo_mode'
const TEST_MODE_KEY = 'test_mode'
const ALL_USER_DATA_KEYS = new Set([
  AUTH_HEADERS_BY_ORG_ID_KEY,
  EXPORT_IN_TRINIUM_MODE_KEY,
  EXPORT_ALL_APPOINTMENTS_KEY,
  USE_OWN_CHASSIS_KEY,
  DEMO_MODE_KEY,
  TEST_MODE_KEY,
  AUTH_HEADER_KEY,
  NEW_AUTH_HEADER_KEY,
])

export const useUserStore = defineStore('user', () => {
  const authHeader = ref(null as null | string)
  const loggedIn = ref(false)
  const readyToConnectToWebSocket = ref(false)
  const authHeadersByOrgID = useLocalStorage<Record<string, string>>(
    AUTH_HEADERS_BY_ORG_ID_KEY,
    {}
  )
  const exportInTriniumMode = useLocalStorage<boolean>(
    EXPORT_IN_TRINIUM_MODE_KEY,
    false
  )
  const exportAllAppointments = useLocalStorage<boolean>(
    EXPORT_ALL_APPOINTMENTS_KEY,
    false
  )
  const orgNotificationsChannel = ref<string>('')
  const userNotificationsChannel = ref<string>('')

  const useOwnChassis = useLocalStorage<boolean>(USE_OWN_CHASSIS_KEY, false)
  const loading = ref(false)
  const feature_flags = ref(new Set<string>())
  const showHideImportAppointments: Ref<boolean | undefined> = ref(true)

  const demo_mode = useLocalStorage<boolean>(DEMO_MODE_KEY, false, {})
  const testMode = useLocalStorage<boolean>(TEST_MODE_KEY, false)
  const userInfo = shallowRef(null as null | UserInfo)
  const currentOrg = shallowRef(null as null | Organization)

  // Computed properties
  const userEmail = computed(() => {
    return userInfo.value?.email ?? null
  })
  const is_internal_user = computed(() => {
    if (userEmail.value) {
      return isInternalUser(userEmail.value)
    } else {
      return false
    }
  })

  // Actions
  function setAuthHeader(header: string) {
    if (header === authHeader.value) return
    authHeader.value = header
    setAxiosAuthorizationHeader(header)
    loggedIn.value = true
    identifyUserWithServices()
  }
  function setAndSaveAuthHeader(header: string, orgID: number) {
    if (header === authHeader.value) return
    setAuthHeader(header)
    authRepo.saveAuthHeader({ header, orgID })
  }
  function loadAuth() {
    const authHeader = authRepo.loadAuthHeader()
    if (authHeader) {
      setAuthHeader(authHeader.header)
    } else {
      // TODO
    }
  }
  loadAuth()

  function addAuthHeader(tokenInfo: GETToken) {
    const authHeaderVal = makeAuthHeader(tokenInfo)
    authHeadersByOrgID.value[tokenInfo.organization_id] = authHeaderVal
  }
  let loadInfoPromise: Promise<UserInfo> | null = null

  function loadInfoFromAPI(): Promise<UserInfo> {
    if (loadInfoPromise) {
      return loadInfoPromise
    }
    const api = new UsersApi()
    const usedHeader = getAxiosAuthorizationHeader()
    if (!usedHeader) throw new Error('User is not logged in')
    loadInfoPromise = api
      .getCurrentUserInfoUsersCurrentInfoGet()
      .then((resp) => {
        const sentHeader = getAxiosAuthorizationHeader()
        if (sentHeader !== usedHeader) {
          throw new Error('Current user changed while loading user info')
        }
        return updateUserAndSaveToStorage(resp.data)
      })
      .finally(() => {
        loadInfoPromise = null
      })

    return loadInfoPromise
  }
  function updateUser(user: GETUserInfo): UserInfo {
    currentOrg.value = new Organization(user.current_organization)
    userInfo.value = new UserInfo(user, demo_mode.value)
    feature_flags.value = new Set<string>(userInfo.value.feature_flags)
    orgNotificationsChannel.value = `private-organization-notifications-${userInfo.value.current_organization.id}`
    userNotificationsChannel.value = `private-user-notifications-${userInfo.value.current_organization.id}-${userInfo.value.id}`
    readyToConnectToWebSocket.value = true
    // We just got new user data so we must be done with our our change
    clearOrgChangeState()

    return userInfo.value
  }
  async function updateUserAndSaveToStorage(
    user: GETUserInfo
  ): Promise<UserInfo> {
    const newUserInfo = updateUser(user)
    await userInfoRepo.saveToStorage(user)
    return newUserInfo
  }

  /**
   * Loads user info from IndexDB storage (which supports large objects, which we have for
   * internal Dray Dog users) if available
   */

  async function loadInfoIfNeeded(): Promise<UserInfo> {
    if (userInfo.value) {
      return userInfo.value
    }
    const cachedUserInfo = await userInfoRepo.loadFromStorage()
    let loadInfoPromise: Promise<UserInfo> | null = null
    if (cachedUserInfo.shouldRefresh) {
      loadInfoPromise = loadInfoFromAPI()
    }
    if (cachedUserInfo.userInfo) {
      return updateUser(cachedUserInfo.userInfo)
    } else if (!loadInfoPromise) {
      loadInfoPromise = loadInfoFromAPI()
    }
    return await loadInfoPromise
  }

  function updateOrg(orgUpdate: GETOrganization) {
    if (userInfo.value) {
      if (userInfo.value.current_organization.id === orgUpdate.id) {
        userInfo.value.current_organization = new Organization(orgUpdate)
      }
      for (const [index, org] of userInfo.value.organizations.entries()) {
        if (org.id === orgUpdate.id) {
          userInfo.value.organizations[index] = org
        }
      }
    }
  }

  async function switchToOrg(orgID: number): Promise<void> {
    console.log('Switching to org: ', orgID)
    setOrgChangeState() // So other tabs know we're changing orgs
    clearWebSocketChannels()
    if (!authHeadersByOrgID.value[orgID]) {
      const api = new UsersApi()
      const resp = await api.getTokenViaTokenUsersTokenViaTokenPost({
        organization_id: orgID,
      })
      authHeadersByOrgID.value[orgID] = makeAuthHeader(resp.data)
    }
    clearLocalStorageExcept(ALL_USER_DATA_KEYS)
    setAndSaveAuthHeader(authHeadersByOrgID.value[orgID], orgID)
    if (!userInfo.value) throw new Error('User info not loaded')
    const newUserInfo = changeOrgInUserInfo(userInfo.value.raw, orgID)
    if (newUserInfo.current_organization.id !== orgID) {
      throw new Error('Organization ID mismatch')
    }
    await updateUserAndSaveToStorage(newUserInfo)
    // Notify other tabs to reload before reloading this tab
    try {
      // Try catch because some old browsers don't support BroadcastChannel
      broadcastOrgChangeToOtherTabs(newUserInfo.current_organization.id)
    } catch (e) {
      console.error('Error broadcasting org change to other tabs', e)
      Sentry.captureException(e)
    }
    reloadPage()
  }

  async function login(tokenResp: GETToken) {
    const authHeader = makeAuthHeader(tokenResp)
    setAndSaveAuthHeader(authHeader, tokenResp.organization_id)
    demo_mode.value = false
    await loadInfoFromAPI()
  }
  async function loginUsingPortOptimizerToken(token: string): Promise<void> {
    const api = new UsersApi()
    return await api
      .getTokenViaPortOptimizerTokenUsersTokenViaPortOptimizerTokenPost({
        token,
      })
      .then((resp) => {
        return login(resp.data)
      })
  }
  function clearWebSocketChannels() {
    readyToConnectToWebSocket.value = false
    orgNotificationsChannel.value = ''
    userNotificationsChannel.value = ''
  }

  function clearAllUserData() {
    clearWebSocketChannels()
    authRepo.saveAuthHeader(undefined)
    userInfo.value = null
    authHeader.value = null
    loggedIn.value = false
    localStorage.clear()
    feature_flags.value = new Set<string>()
    userInfoRepo.clearFromStorage()
  }

  function logout() {
    clearAxiosAuthorizationHeader()
    clearAllUserData()
    clearChatData()
    // @ts-expect-error
    if (window.posthog) {
      // @ts-expect-error
      posthog.reset()
    }
  }
  const userAlreadyIdentified = ref(false)
  async function identifyUserWithServices() {
    if (!userInfo.value) return
    // Avoid duplicate calls to these services. If our page is refreshed, this would
    // be called again.
    if (userAlreadyIdentified.value) return
    const email = userInfo.value?.email
    if (import.meta.env.DEV) {
      return
    }
    configureUserWithCrispChat(userInfo.value)
    if (isInternalUser(email)) {
      return
    }
    const openReplayModule = await import('~/observability/openReplay')
    const openReplay = openReplayModule.getOpenReplayTracker()
    openReplay.setUserID(email)
    startOpenReplayTracking().then(() => {
      openReplay.setUserID(email)
    })
    // Identify in PostHog. PostHog is not available on localhost
    // @ts-expect-error
    if (window.posthog) {
      // Delay is to give PostHog time to initialize
      setTimeout(() => {
        // @ts-expect-error
        posthog.identify(email, {
          properties: {
            email,
          },
        })
      }, 2000)
    } else {
      console.log('PostHog not available')
    }
    // Identify in Sentry (error tracking)
    Sentry.setUser({ email })
    userAlreadyIdentified.value = true
  }

  function setShowHideImportAppt(value: boolean | undefined) {
    showHideImportAppointments.value = value
  }

  // Getters
  const token = computed((): string | undefined => {
    if (authHeader.value) {
      return authHeader.value.split(' ')[1]
    }
    return undefined
  })
  const currentRole = computed((): UserRoleType | null => {
    return userInfo.value?.current_organization.role ?? null
  })
  const isSubCarrier = computed((): boolean => {
    return currentRole.value === UserRoleType.SubCarrier
  })
  const hasManagementPermissions = computed((): boolean => {
    return (
      currentRole.value === UserRoleType.Management ||
      (userEmail.value !== null && isInternalUser(userEmail.value))
    )
  })
  const loggedInUser = computed((): SimpleUser | null => {
    if (userInfo.value) {
      return {
        id: userInfo.value.id,
        first_name: userInfo.value.first_name,
        last_name: userInfo.value.last_name,
      }
    }
    return null
  })

  const isTerminalOrg = computed((): boolean => {
    if (userInfo.value) {
      // if its a terminal subscription
      const terminal_org =
        userInfo.value.current_organization.subscriptions.includes(
          OrganizationSubscription.Terminal
        )
      return terminal_org
    }
    return false
  })

  const isUasOrg = computed((): boolean => {
    if (userInfo.value) {
      // if its a UAS subscription
      const uas_org =
        userInfo.value.current_organization.subscriptions.includes(
          OrganizationSubscription.Uas
        )
      return uas_org
    }
    return false
  })
  const isUASOnlyOrg = computed((): boolean => {
    if (userInfo.value) {
      return (
        userInfo.value.current_organization.subscriptions.length === 1 &&
        isUasOrg.value
      )
    }
    return false
  })

  const getShowHideImportAppt = computed((): boolean | undefined => {
    return showHideImportAppointments.value
  })

  return {
    // Refs
    userEmail,
    authHeader,
    exportInTriniumMode,
    exportAllAppointments,
    useOwnChassis,
    loading,
    feature_flags,
    demo_mode,
    testMode,
    userInfo,
    is_internal_user,
    showHideImportAppointments,
    orgNotificationsChannel,
    userNotificationsChannel,
    // Actions
    addAuthHeader,
    switchToOrg,
    loadInfoIfNeeded,
    login,
    logout,
    identifyUserWithServices,
    loadInfoFromAPI,
    updateUserAndSaveToStorage,
    updateOrg,
    updateUser,
    setShowHideImportAppt,
    loginUsingPortOptimizerToken,
    // Computed properties
    loggedIn,
    readyToConnectToWebSocket,
    token,
    isSubCarrier,
    hasManagementPermissions,
    loggedInUser,
    currentOrg,
    getShowHideImportAppt,
    isTerminalOrg,
    isUasOrg,
    isUASOnlyOrg,
  }
})

function makeAuthHeader(tokenResp: GETToken): string {
  return `${tokenResp.token_type} ${tokenResp.access_token}`
}

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
/**
 * Reconfigures a user infor object so the current organization is different.
 *
 * This is much more efficient than making another API call to get the new user info
 *
 * @param userInfo - The current user information object
 * @param orgID - The ID of the organization to switch to
 * @returns A new user info object with the updated current organization
 * @throws Error if the specified organization ID is not found in the user's organizations
 */
function changeOrgInUserInfo(
  userInfo: GETUserInfo,
  orgID: number
): GETUserInfo {
  if (userInfo.current_organization.id === orgID) {
    return userInfo // Nothing to change
  }
  for (const org of userInfo.organizations) {
    if (org.id === orgID) {
      return {
        ...userInfo,
        current_organization: org,
      }
    }
  }
  throw new Error(`Organization with ID ${orgID} not found in user info`)
}
